import { useState } from 'react'
import { 
  ChevronDown, 
  ChevronUp,
  Edit,
  Save,
  MapPin,
  Calendar,
  DollarSign,
  Users,
  Briefcase,
  Clock,
  Star,
  Building,
  Globe,
  Share2,
  Copy,
  Check,
  Loader,
  Tag,
  Plus,
  Trash2
} from 'lucide-react'
import SkillsSelector from '@/components/common/SkillsSelector'

// Simple inline certification editor component
const CertificationEditor = ({ 
  certifications, 
  onCertificationsChange 
}: { 
  certifications: PositionCertification[], 
  onCertificationsChange: (certs: PositionCertification[]) => void 
}) => {
  const [newCert, setNewCert] = useState<PositionCertification>({
    certification_name: '',
    certification_body: '',
    is_required: true,
    expiry_required: false
  })

  const addCertification = () => {
    if (newCert.certification_name.trim()) {
      onCertificationsChange([...certifications, newCert])
      setNewCert({
        certification_name: '',
        certification_body: '',
        is_required: true,
        expiry_required: false
      })
    }
  }

  const removeCertification = (index: number) => {
    const updated = certifications.filter((_, i) => i !== index)
    onCertificationsChange(updated)
  }

  return (
    <div className="space-y-4">
      {/* Existing Certifications */}
      {certifications.map((cert, index) => (
        <div key={index} className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <span className="font-medium">{cert.certification_name}</span>
              {cert.certification_body && (
                <span className="text-sm text-gray-600">by {cert.certification_body}</span>
              )}
              <span className={`px-2 py-1 text-xs rounded-full ${
                cert.is_required ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
              }`}>
                {cert.is_required ? 'Required' : 'Preferred'}
              </span>
            </div>
          </div>
          <button
            onClick={() => removeCertification(index)}
            className="text-red-600 hover:text-red-800 p-1"
          >
            ×
          </button>
        </div>
      ))}

      {/* Add New Certification */}
      <div className="space-y-3 p-4 border border-gray-200 rounded-lg">
        <h4 className="font-medium text-gray-900">Add Certification</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="Certification name"
            value={newCert.certification_name}
            onChange={(e) => setNewCert({ ...newCert, certification_name: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Certification body (optional)"
            value={newCert.certification_body || ''}
            onChange={(e) => setNewCert({ ...newCert, certification_body: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={newCert.is_required}
              onChange={(e) => setNewCert({ ...newCert, is_required: e.target.checked })}
              className="mr-2"
            />
            Required
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={newCert.expiry_required || false}
              onChange={(e) => setNewCert({ ...newCert, expiry_required: e.target.checked })}
              className="mr-2"
            />
            Valid certification required
          </label>
        </div>
        <button
          onClick={addCertification}
          disabled={!newCert.certification_name.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add Certification
        </button>
      </div>
    </div>
  )
}

interface PositionSkill {
  skill_id: number
  skill_name: string
  category: string
  proficiency_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'
  is_good_to_have?: boolean
}

interface PositionCertification {
  certification_name: string
  certification_body?: string
  is_required: boolean
  expiry_required?: boolean
}

interface Position {
  id: string
  title: string
  description: string
  department: string
  location: string
  type: string
  status: string
  is_public: boolean
  public_url: string | null
  skills_required?: PositionSkill[]
  certifications?: PositionCertification[]
  salary: {
    min: number
    max: number
    currency: string
  }
  candidates: {
    total: number
    new: number
    interviewed: number
    shortlisted: number
    hired: number
  }
  datePosted: string
  deadline: string
  priority: string
  requirements?: string[]
  recruiter: string
}

interface CollapsiblePositionDetailsProps {
  position: Position
  isEditing: boolean
  saving: boolean
  copied: boolean
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
  onInputChange: (field: keyof Position, value: any) => void
  onSalaryChange: (field: 'min' | 'max' | 'currency', value: any) => void
  onSkillsChange: (skills: PositionSkill[]) => void
  onCertificationsChange: (certifications: PositionCertification[]) => void
  copyPublicUrl: () => void
  getStatusColor: (status: string) => string
  getPriorityColor: (priority: string) => string
}

const CollapsiblePositionDetails = ({
  position,
  isEditing,
  saving,
  copied,
  onEdit,
  onSave,
  onCancel,
  onInputChange,
  onSalaryChange,
  onSkillsChange,
  onCertificationsChange,
  copyPublicUrl,
  getStatusColor,
  getPriorityColor
}: CollapsiblePositionDetailsProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [newRequirement, setNewRequirement] = useState('')

  const getCategoryColor = (category: string) => {
    const colors = {
      'Programming Languages': 'bg-red-100 text-red-800',
      'Frontend': 'bg-blue-100 text-blue-800',
      'Backend': 'bg-green-100 text-green-800',
      'Database': 'bg-yellow-100 text-yellow-800',
      'Cloud': 'bg-purple-100 text-purple-800',
      'DevOps': 'bg-orange-100 text-orange-800',
      'Mobile': 'bg-pink-100 text-pink-800',
      'Data Science': 'bg-indigo-100 text-indigo-800'
    }
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  const getProficiencyColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-gray-100 text-gray-800'
      case 'intermediate': return 'bg-blue-100 text-blue-800'
      case 'advanced': return 'bg-green-100 text-green-800'
      case 'expert': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Collapsed Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="flex items-center space-x-3">
                {isEditing ? (
                  <input
                    type="text"
                    value={position.title}
                    onChange={(e) => onInputChange('title', e.target.value)}
                    className="text-xl font-bold text-gray-900 bg-transparent border-b-2 border-blue-500 focus:outline-none"
                    placeholder="Position Title"
                  />
                ) : (
                  <h1 className="text-xl font-bold text-gray-900">{position.title}</h1>
                )}
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(position.status)}`}>
                  {position.status === 'open' ? 'Open (More Needed)' : 
                   position.status === 'closed' ? 'Closed (Vacancy Filled)' : 
                   position.status.charAt(0).toUpperCase() + position.status.slice(1)}
                </span>
                <div className={`flex items-center ${getPriorityColor(position.priority)}`}>
                  <Star className="w-3 h-3 fill-current mr-1" />
                  <span className="text-xs capitalize">{position.priority}</span>
                </div>
              </div>
              
              {isCollapsed && (
                <div className="flex items-center space-x-6 mt-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Building className="w-4 h-4 mr-1" />
                    {position.department}
                  </div>
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {position.location}
                  </div>
                  <div className="flex items-center">
                    <DollarSign className="w-4 h-4 mr-1" />
                    {position.salary.min.toLocaleString()}-{position.salary.max.toLocaleString()} {position.salary.currency}
                  </div>
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-1" />
                    {position.candidates.total} candidates
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={onCancel}
                  disabled={saving}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={onSave}
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </>
                  )}
                </button>
              </>
            ) : (
              <button 
                onClick={() => {
                  onEdit()
                  setIsCollapsed(false)
                }} 
                className="btn-primary"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </button>
            )}
            
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              {isCollapsed ? (
                <ChevronDown className="w-5 h-5" />
              ) : (
                <ChevronUp className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {!isCollapsed && (
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Position Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Position Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Position Title</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={position.title}
                        onChange={(e) => onInputChange('title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter position title"
                      />
                    ) : (
                      <div className="flex items-center">
                        <Briefcase className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-gray-900 font-medium">{position.title}</span>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    {isEditing ? (
                      <textarea
                        value={position.description}
                        onChange={(e) => onInputChange('description', e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    ) : (
                      <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {position.description}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Requirements / Must-haves</label>
                    {isEditing ? (
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={newRequirement}
                            onChange={(e) => setNewRequirement(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                const trimmed = newRequirement.trim()
                                if (trimmed) {
                                  onInputChange('requirements', [...(position.requirements || []), trimmed])
                                  setNewRequirement('')
                                }
                              }
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Add a requirement"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const trimmed = newRequirement.trim()
                              if (!trimmed) return
                              onInputChange('requirements', [...(position.requirements || []), trimmed])
                              setNewRequirement('')
                            }}
                            className="inline-flex items-center px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </button>
                        </div>

                        <div className="space-y-2">
                          {(position.requirements || []).map((req, idx) => (
                            <div key={idx} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                              <input
                                type="text"
                                value={req}
                                onChange={(e) => {
                                  const updated = [...(position.requirements || [])]
                                  updated[idx] = e.target.value
                                  onInputChange('requirements', updated)
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = (position.requirements || []).filter((_, i) => i !== idx)
                                  onInputChange('requirements', updated)
                                }}
                                className="p-2 text-red-600 hover:text-red-800"
                                aria-label="Remove requirement"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          {(position.requirements || []).length === 0 && (
                            <p className="text-sm text-gray-500">No requirements added yet.</p>
                          )}
                        </div>
                      </div>
                    ) : position.requirements && position.requirements.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {position.requirements.map((req, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm"
                          >
                            <Tag className="w-4 h-4 mr-1" />
                            {req}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No requirements listed.</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={position.department}
                          onChange={(e) => onInputChange('department', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <div className="flex items-center">
                          <Building className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-900">{position.department}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={position.location}
                          onChange={(e) => onInputChange('location', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-900">{position.location}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      {isEditing ? (
                        <select
                          value={position.status}
                          onChange={(e) => onInputChange('status', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="draft">Draft</option>
                          <option value="open">Open (More Needed)</option>
                          <option value="paused">Paused</option>
                          <option value="closed">Closed (Vacancy Filled)</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(position.status)}`}>
                          {position.status === 'open' ? 'Open (More Needed)' : 
                           position.status === 'closed' ? 'Closed (Vacancy Filled)' : 
                           position.status.charAt(0).toUpperCase() + position.status.slice(1)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Salary Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Compensation</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min Salary</label>
                    {isEditing ? (
                      <input
                        type="number"
                        value={position.salary.min}
                        onChange={(e) => onSalaryChange('min', Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 text-gray-400 mr-1" />
                        <span className="text-gray-900">{position.salary.min.toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max Salary</label>
                    {isEditing ? (
                      <input
                        type="number"
                        value={position.salary.max}
                        onChange={(e) => onSalaryChange('max', Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    ) : (
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 text-gray-400 mr-1" />
                        <span className="text-gray-900">{position.salary.max.toLocaleString()}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                    {isEditing ? (
                      <select
                        value={position.salary.currency}
                        onChange={(e) => onSalaryChange('currency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="USD">USD (US Dollar)</option>
                        <option value="EUR">EUR (Euro)</option>
                        <option value="GBP">GBP (British Pound)</option>
                        <option value="INR">INR (Indian Rs)</option>
                        <option value="CAD">CAD (Canadian Dollar)</option>
                        <option value="AUD">AUD (Australian Dollar)</option>
                        <option value="JPY">JPY (Japanese Yen)</option>
                        <option value="SGD">SGD (Singapore Dollar)</option>
                      </select>
                    ) : (
                      <span className="text-gray-900">{position.salary.currency}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Tag className="w-5 h-5 mr-2" />
                  Skills & Requirements
                </h3>
                {isEditing ? (
                  <SkillsSelector
                    selectedSkills={position.skills_required?.map(skill => ({
                      id: skill.skill_id,
                      skill_name: skill.skill_name,
                      category: skill.category,
                      proficiency_level: skill.proficiency_level,
                      is_good_to_have: skill.is_good_to_have || false
                    })) || []}
                    onSkillsChange={(skills) => {
                      const positionSkills: PositionSkill[] = skills.map(skill => ({
                        skill_id: skill.id,
                        skill_name: skill.skill_name,
                        category: skill.category,
                        proficiency_level: skill.proficiency_level,
                        is_good_to_have: skill.is_good_to_have || false
                      }))
                      onSkillsChange(positionSkills)
                    }}
                    placeholder="Select skills for this position..."
                  />
                ) : (
                  <div>
                    {position.skills_required && position.skills_required.length > 0 ? (
                      <div className="space-y-4">
                        {/* Required Skills */}
                        <div>
                          <h4 className="font-medium text-red-700 mb-2">Required Skills</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {position.skills_required
                              .filter(skill => !skill.is_good_to_have)
                              .map((skill, index) => (
                                <div
                                  key={`required-${skill.skill_id}-${index}`}
                                  className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"
                                >
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium text-gray-900">{skill.skill_name}</span>
                                    <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(skill.category)}`}>
                                      {skill.category}
                                    </span>
                                  </div>
                                  <span className={`px-2 py-1 text-xs rounded-full ${getProficiencyColor(skill.proficiency_level)}`}>
                                    {skill.proficiency_level}
                                  </span>
                                </div>
                              ))}
                          </div>
                          {position.skills_required.filter(skill => !skill.is_good_to_have).length === 0 && (
                            <p className="text-gray-500 text-sm">No required skills specified</p>
                          )}
                        </div>

                        {/* Good-to-Have Skills */}
                        <div>
                          <h4 className="font-medium text-green-700 mb-2">Good-to-Have Skills</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {position.skills_required
                              .filter(skill => skill.is_good_to_have)
                              .map((skill, index) => (
                                <div
                                  key={`good-to-have-${skill.skill_id}-${index}`}
                                  className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                                >
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium text-gray-900">{skill.skill_name}</span>
                                    <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(skill.category)}`}>
                                      {skill.category}
                                    </span>
                                  </div>
                                  <span className={`px-2 py-1 text-xs rounded-full ${getProficiencyColor(skill.proficiency_level)}`}>
                                    {skill.proficiency_level}
                                  </span>
                                </div>
                              ))}
                          </div>
                          {position.skills_required.filter(skill => skill.is_good_to_have).length === 0 && (
                            <p className="text-gray-500 text-sm">No good-to-have skills specified</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No skills specified for this position</p>
                    )}
                  </div>
                )}
              </div>

              {/* Certifications */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Tag className="w-5 h-5 mr-2" />
                  Certifications
                </h3>
                {isEditing ? (
                  <CertificationEditor
                    certifications={position.certifications || []}
                    onCertificationsChange={onCertificationsChange}
                  />
                ) : (
                  <div>
                    {position.certifications && position.certifications.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {position.certifications.map((cert, index) => (
                          <div
                            key={`cert-${index}`}
                            className={`flex items-center justify-between p-3 rounded-lg ${
                              cert.is_required 
                                ? 'bg-yellow-50 border border-yellow-200' 
                                : 'bg-blue-50 border border-blue-200'
                            }`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-gray-900">{cert.certification_name}</span>
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  cert.is_required 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {cert.is_required ? 'Required' : 'Preferred'}
                                </span>
                              </div>
                              {cert.certification_body && (
                                <p className="text-sm text-gray-600 mt-1">by {cert.certification_body}</p>
                              )}
                              {cert.expiry_required && (
                                <p className="text-xs text-orange-600 mt-1">Valid certification required</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No certifications specified</p>
                    )}
                  </div>
                )}
              </div>

              {/* Public Job Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Public Job Board</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Globe className="w-5 h-5 text-gray-400 mr-3" />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Make this position public</span>
                        <p className="text-xs text-gray-500">Allow external candidates to apply through the job board</p>
                      </div>
                    </div>
                    {isEditing ? (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={position.is_public}
                          onChange={(e) => onInputChange('is_public', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    ) : (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        position.is_public 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {position.is_public ? 'Public' : 'Private'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Public Sharing */}
              {position.is_public && position.public_url && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Public Sharing</h3>
                  <div className="flex items-center space-x-2 mb-2">
                    <Share2 className="w-4 h-4 text-gray-600" />
                    <p className="text-sm font-medium text-gray-900">Public URL</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-700 font-mono truncate">{position.public_url}</p>
                    </div>
                    <button
                      onClick={copyPublicUrl}
                      className="flex items-center space-x-1 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Stats */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Overview</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Total Candidates</span>
                    <span className="text-lg font-semibold text-gray-900">{position.candidates.total}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">New Applications</span>
                    <span className="text-lg font-semibold text-blue-600">{position.candidates.new}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Interviewed</span>
                    <span className="text-lg font-semibold text-purple-600">{position.candidates.interviewed}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Hired</span>
                    <span className="text-lg font-semibold text-green-600">{position.candidates.hired}</span>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">Posted:</span>
                    <span className="text-gray-900">
                      {new Date(position.datePosted).toLocaleDateString()}
                    </span>
                  </div>
                  {position.deadline && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">Deadline:</span>
                      <span className="text-gray-900">
                        {new Date(position.deadline).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CollapsiblePositionDetails
